/**
 * Lo-fi illustration of the author workbench for the marketing
 * product-preview slot (PO decision 2026-08-20: deliberately NOT a
 * screenshot — no real navigation, feature names, or readable text, so
 * nothing internal can leak and the art never goes stale).
 *
 * Abstraction contract: the only concepts depicted are ones the landing
 * page already states publicly — pen names, projects, books/drafts, the
 * five stages, word counts. Every "text" is a greeked bar. Purely
 * decorative (aria-hidden); the section supplies the accessible label.
 */

/** Greeked text line. */
function Bar({ w, h = 8, tone = "bg-stone-200" }: { w: string; h?: number; tone?: string }) {
  return <span className={`block rounded-full ${tone}`} style={{ width: w, height: h }} />
}

function StageDots({ done, active }: { done: number; active?: boolean }) {
  return (
    <span className="flex items-center gap-1.5">
      {Array.from({ length: 5 }, (_, i) => (
        <span
          key={i}
          className={
            i < done
              ? "size-[7px] rounded-full bg-rose-600"
              : i === done && active
                ? "size-[7px] rounded-full border-[1.5px] border-rose-600 bg-rose-100"
                : "size-[7px] rounded-full bg-stone-200"
          }
        />
      ))}
    </span>
  )
}

function ProjectCard({
  done,
  progress,
  active = false,
  titleWidth,
}: {
  done: number
  progress: string
  active?: boolean
  titleWidth: string
}) {
  return (
    <div
      className={
        active
          ? "flex flex-col gap-2.5 rounded-[14px] border border-rose-200 bg-white p-3.5 shadow-[0_8px_20px_-10px_rgb(225_29_72/0.25)]"
          : "flex flex-col gap-2.5 rounded-[14px] border border-stone-200 bg-white p-3.5"
      }
    >
      {/* Serif title energy = a taller greeked bar */}
      <Bar w={titleWidth} h={11} tone="bg-stone-300" />
      <Bar w="55%" h={7} />
      <StageDots done={done} active={active} />
      <div className="mt-0.5 h-[6px] w-full overflow-hidden rounded-full bg-stone-100">
        <div
          className={
            active
              ? "h-full rounded-full bg-rose-600 motion-safe:animate-[workbenchFill_1.2s_cubic-bezier(0.22,1,0.36,1)_both]"
              : "h-full rounded-full bg-stone-300"
          }
          style={{ width: progress }}
        />
      </div>
      <div className="flex items-center justify-between">
        <Bar w="34%" h={6} />
        <Bar w="18%" h={6} tone="bg-stone-300" />
      </div>
    </div>
  )
}

export function WorkbenchIllustration() {
  return (
    <div
      aria-hidden
      className="flex aspect-video w-full overflow-hidden rounded-[20px] border border-stone-200 bg-[#f7f6f4] text-left select-none"
    >
      {/* Left rail: identity + pen names + greeked nav */}
      <div className="hidden w-[21%] shrink-0 flex-col gap-4 border-r border-stone-200 bg-white/70 p-4 sm:flex">
        <div className="flex items-center gap-2">
          <span className="size-[18px] rounded-full bg-rose-600" />
          <Bar w="56%" h={9} tone="bg-stone-300" />
        </div>

        <div className="mt-1 flex flex-col gap-2">
          <Bar w="40%" h={6} />
          {/* Pen-name chips */}
          <div className="flex items-center gap-2 rounded-full border border-stone-200 bg-white py-1.5 pr-3 pl-1.5">
            <span className="grid size-[18px] place-items-center rounded-full bg-rose-100 text-[8px] font-bold text-rose-700">
              a
            </span>
            <Bar w="58%" h={7} />
          </div>
          <div className="flex items-center gap-2 rounded-full border border-stone-200 bg-white py-1.5 pr-3 pl-1.5">
            <span className="grid size-[18px] place-items-center rounded-full bg-stone-200 text-[8px] font-bold text-stone-500">
              j
            </span>
            <Bar w="44%" h={7} />
          </div>
        </div>

        <div className="mt-3 flex flex-col gap-2.5">
          <Bar w="70%" h={7} />
          <Bar w="52%" h={7} />
          <Bar w="62%" h={7} />
          <Bar w="44%" h={7} />
        </div>

        <div className="mt-auto flex flex-col gap-2">
          <div className="h-px w-full bg-stone-200" />
          <div className="flex items-center gap-2 pt-1">
            <span className="size-[16px] rounded-full bg-stone-200" />
            <Bar w="52%" h={6} />
          </div>
        </div>
      </div>

      {/* Main area */}
      <div className="flex min-w-0 flex-1 flex-col gap-3.5 p-4 sm:p-5">
        {/* Header: greeked heading + rose CTA pill */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-col gap-1.5">
            <Bar w="140px" h={12} tone="bg-stone-300" />
            <Bar w="90px" h={7} />
          </div>
          <span className="h-[30px] w-[86px] rounded-full bg-rose-600" />
        </div>

        {/* Project cards: each with five stage dots + progress + word count */}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
          <ProjectCard done={4} active progress="72%" titleWidth="78%" />
          <ProjectCard done={5} progress="100%" titleWidth="62%" />
          <div className="hidden lg:block">
            <ProjectCard done={2} progress="34%" titleWidth="70%" />
          </div>
        </div>

        {/* Draft pane: chapter list + greeked prose */}
        <div className="flex min-h-0 flex-1 gap-3">
          <div className="hidden w-[30%] flex-col gap-2 overflow-hidden rounded-[14px] border border-stone-200 bg-white p-3 md:flex">
            <Bar w="50%" h={8} tone="bg-stone-300" />
            {["82%", "74%", "78%", "68%", "80%", "72%", "76%", "66%", "70%", "79%", "73%", "77%", "69%", "75%", "71%", "67%", "81%", "65%", "78%", "72%", "76%"].map((w, i) => (
              <div key={i} className="flex items-center gap-2">
                <span
                  className={
                    i < 4 ? "size-[6px] shrink-0 rounded-full bg-rose-600" : "size-[6px] shrink-0 rounded-full bg-stone-300"
                  }
                />
                <Bar w={w} h={6} />
              </div>
            ))}
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-2 overflow-hidden rounded-[14px] border border-stone-200 bg-white p-3.5 sm:p-4">
            <Bar w="34%" h={10} tone="bg-stone-300" />
            <div className="mt-1 flex flex-col gap-[7px]">
              {[
                "96%", "91%", "94%", "62%", "0",
                "88%", "95%", "90%", "70%", "0",
                "92%", "89%", "96%", "84%", "58%", "0",
                "94%", "90%", "93%", "66%", "0",
                "89%", "96%", "91%", "87%", "74%", "0",
                "95%", "88%", "92%", "52%",
              ].map((w, i) =>
                w === "0" ? (
                  <span key={i} className="h-[5px]" />
                ) : (
                  <Bar
                    key={i}
                    w={w}
                    h={6}
                    tone={i < 16 ? "bg-stone-200" : "bg-stone-200/70"}
                  />
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
